/* eslint-disable @typescript-eslint/no-explicit-any */
import { OpalApiComponent } from '@opallabs/api'

type APIMoment = OpalApiComponent<'V2', 'moment'>
type APIContent = OpalApiComponent<'V2', 'content'>
type APIService = OpalApiComponent<'V2', 'service'>
type APILabel = OpalApiComponent<'V2', 'label'>

/**
 * We don't have a great typing Moments, and _especially_
 * a moment after it goes through our various redux pipeline.
 *
 * For now, I'm compiling together a new type based on the properties
 * I know the moment response _should_ have, and will use type guarding
 * to assert that these properties exist at runtime.
 */
export type PartialMoment = {
  id: APIMoment['id']
  day_segment?: APIMoment['attributes']['day_segment']
  scheduled_at?: APIMoment['attributes']['scheduled_at']
  scheduled_end_at?: APIMoment['attributes']['scheduled_end_at']
  account?: {
    id: string
    name?: string
  } | null
  labels?: {
    id: APILabel['id']
    name?: APILabel['attributes']['name']
    color?: APILabel['attributes']['color']
    label_set?: {
      id?: string
      name?: string
    }
  }[]
  services?: {
    id: APIService['id']
    system_name?: APIService['attributes']['system_name']
  }[]
  // Fields below are set on the Redux-hydrated moment (not on the raw JSON:API
  // resource) but are read by both moment- and content-mode card components.
  // Listing them here lets callers read the hydrated fields without local
  // casts; they remain optional because the raw API response still omits them.
  title?: string
  isPrivate?: boolean
  isUpdatingContent?: boolean
  storyNames?: string[]
  primaryStory?: {
    name: string
    color: string
    scheduled_start_at?: string
    scheduled_end_at?: string
  }
  primaryBrand?: { slug?: string }
  primary_asset_reference?: {
    preview_url?: string
    full_url?: string
  }
}

export type PartialStory = {
  name: string
  color: string
}

// Subset of an asset reference's fields needed to resolve a preview image
// via `getRelevantImageProperties` (proxy URL is built from `uuid`).
export type AssetReference = {
  uuid?: string
  asset_type?: string
  format?: string
  height?: number
  width?: number
  cover_asset_reference?: AssetReference
}

/**
 * Mirrors `PartialMoment` but for individual content items (posts). Used by
 * Content mode to render content-primary cards keyed by each item's own
 * `scheduled_at` rather than the parent moment's.
 */
export type PartialContent = {
  id: APIContent['id']
  scheduled_at?: APIContent['attributes']['scheduled_at']
  start_interval?: APIContent['attributes']['start_interval']
  takedown_scheduled_at?: APIContent['attributes']['takedown_scheduled_at']
  name?: string
  asset_references?: AssetReference[]
  // Workflow status. Null on redacted/ghost content, which the approval-status
  // helpers fall back to 'is-draft' for.
  status?: PartialPost['status'] | null
  is_published?: boolean
  // Optimistic-post flag — set on synthetic `{id: null, isSaving: true}`
  // entries that `collectOptimisticPosts` surfaces in Content mode while a
  // duplicate/create round-trip is in flight.
  isSaving?: boolean
  account?: {
    id: string
    name?: string
  } | null
  service?: {
    id: APIService['id']
    name?: APIService['attributes']['name']
    system_name?: APIService['attributes']['system_name']
  }
  labels?: {
    id: APILabel['id']
    name?: APILabel['attributes']['name']
    color?: APILabel['attributes']['color']
    label_set?: {
      id?: string
      name?: string
    }
  }[]
  relationships?: {
    moment?: { id: string }
  }
}

export type PartialPost = {
  id: string
  name: string
  service: ServiceWithSystemName
  isSaving: boolean
  status: 'draft' | 'for_approval' | 'needs_revision' | 'approved'
  is_published: boolean
  post_state?: string
  scheduled_at?: string
  publishing_mode?: string
}

/**
 * Partial types used for Type Assertion
 */
type MomentWithDaySegment = {
  day_segment: string | null
}

type MomentWithScheduledAt = {
  scheduled_at: string
}

type MomentWithServices = {
  services: any[]
}

type MomentWithLabels = {
  labels: any[]
}

type MomentWithAccount = {
  account: { name: string }
}

export type ServiceWithSystemName = {
  id: string
  system_name: string
}

type LabelWithLabelSet = {
  name: string
  label_set: {
    name: string
    id?: string
  }
}

/**
 * Custom Type Assertion functions for client-side validation of Moment entities.
 * These methods will help narrow down if the response from the API is what we structurally
 * expect in situations where the full API response cannot be strongly typed.
 */
export function momentHasScheduledAt<T extends { scheduled_at?: any }>(
  moment: T
): moment is T & MomentWithScheduledAt {
  return typeof moment.scheduled_at === 'string'
}

export function momentHasDaySegment<T extends { day_segment?: any }>(
  moment: T
): moment is T & MomentWithDaySegment {
  return typeof moment.day_segment === 'string'
}
export function momentHasServiceArray<T extends { services?: any[] }>(
  moment: T
): moment is T & MomentWithServices {
  return typeof moment.services === 'object' && Array.isArray(moment.services)
}

export function momentHasLabelArray<T extends { labels?: any[] }>(
  moment: T
): moment is T & MomentWithLabels {
  return typeof moment.labels === 'object' && Array.isArray(moment.labels)
}

export function momentHasAccount<T extends { account?: any }>(
  moment: T
): moment is T & MomentWithAccount {
  return (
    typeof moment.account === 'object' &&
    moment.account !== null &&
    typeof moment.account.name === 'string'
  )
}

// ───────────────────────────────────────────────────────────────────────────
// Type assertions for PartialContent (Content mode category getters)
// ───────────────────────────────────────────────────────────────────────────

type ContentWithScheduledAt = { scheduled_at: string }
type ContentWithTakedown = { takedown_scheduled_at: string }
type ContentWithService = { service: ServiceWithSystemName }

export function contentHasScheduledAt<T extends { scheduled_at?: any }>(
  content: T
): content is T & ContentWithScheduledAt {
  return typeof content.scheduled_at === 'string'
}

/**
 * `start_interval` (mirroring v3's content_schedule) is null when no specific
 * time has been chosen for the post — `scheduled_at` is then just the parent
 * moment's midnight start. `undefined` means the field wasn't fetched (e.g.
 * optimistic entries), so treat it as having a time to keep hour placement.
 */
export function contentHasScheduledTime<T extends { start_interval?: any }>(
  content: T
): boolean {
  return content.start_interval !== null
}

export function contentHasTakedown<T extends { takedown_scheduled_at?: any }>(
  content: T
): content is T & ContentWithTakedown {
  return typeof content.takedown_scheduled_at === 'string'
}

export function contentHasService<T extends { service?: any }>(
  content: T
): content is T & ContentWithService {
  return (
    typeof content.service === 'object' &&
    content.service !== null &&
    typeof content.service.system_name === 'string'
  )
}

type ContentWithAccount = MomentWithAccount
type ContentWithLabels = MomentWithLabels

export function contentHasAccount<T extends { account?: any }>(
  content: T
): content is T & ContentWithAccount {
  return (
    typeof content.account === 'object' &&
    content.account !== null &&
    typeof content.account.name === 'string'
  )
}

export function contentHasLabelArray<T extends { labels?: any[] }>(
  content: T
): content is T & ContentWithLabels {
  return typeof content.labels === 'object' && Array.isArray(content.labels)
}

export function serviceHasSystemName<T extends { system_name?: any }>(
  service: T
): service is T & ServiceWithSystemName {
  return typeof service.system_name === 'string'
}

export function labelHasNameAndSet<
  T extends { label_set?: { name?: any }; name?: any },
>(label: T): label is T & LabelWithLabelSet {
  return (
    typeof label.name === 'string' &&
    'label_set' in label &&
    typeof label.label_set === 'object' &&
    'name' in label.label_set &&
    label.label_set.name !== undefined &&
    typeof label.label_set.name === 'string'
  )
}
